/**
 * No building, inside Manifest Review (2026-10-13).
 *
 * Residents with no confirmed building: those eMerge created from CounselEAR, and
 * -- since 2026-10-14, when Isaiah Cotto ruled "Confirming the clinic should place
 * them." -- any resident (Monday's, a PA's) who carries a CounselEAR clinic and
 * has no building at all.
 * Nothing created a resident after the Monday ingest stopped (2026-09-29);
 * eMerge now creates them from CounselEAR and places each at their CounselEAR
 * Preferred Clinic's building (Operations ruling, April Torino, 2026-10-13)
 * once a person has ratified which building that clinic is. Until then a new
 * resident belongs to no facility, so no building's manifest, census or
 * worklist can show them. This list is where they are seen.
 *
 * Read-only, and an enterprise reviewer's: a facility-scoped reviewer may not
 * see a resident who belongs to no facility, and is told so in words rather
 * than shown an empty list. Each row says what eMerge knows about where the
 * resident might live -- their clinic, the building it has been ratified as
 * since, a manifest that listed them -- and what places them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { refusalWords } from './resubmittalsApi';
import { noBuildingApi, type NoBuildingList, type NoBuildingResident } from './noBuildingApi';
import MfBoard, { useOpenParam, useClientSort, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import { ResidentName, residentLabel } from './board/residents';
import { ToneChip } from './board/chips';
import NoBuildingClinics from './NoBuildingClinics';
// 2026-10-28 (docs/provider/CE_PULL.md): what providers said that disagrees with a resident's building.
import ProviderStatements from './ProviderStatements';
import { clinicWords } from '../../components/emerge/BuildingOrClinic';

const ZONE = 'America/New_York';
const ORIGIN_WORDS: Record<string, string> = { COUNSELEAR: 'CounselEAR', MONDAY: 'Monday', PATIENT_ADVOCATE: 'A PA' };
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ZONE }) : '—');

/** The resident's CounselEAR clinic by name, else by id (2026-10-14: never missing). */
const clinicOf = (r: NoBuildingResident) => (r.clinic_name ? r.clinic_name : r.clinic_id ? `Clinic ${r.clinic_id}` : null);

/** The clinic, labelled as not a confirmed building: the same words every other screen uses (BuildingOrClinic). */
const clinicPhrase = (r: NoBuildingResident) => clinicWords({ counselear_clinic_id: r.clinic_id, counselear_clinic_name: r.clinic_name || null }) || 'No clinic';

/** What eMerge knows about where this resident lives, in one phrase. */
function whereWords(r: NoBuildingResident): string {
  if (r.clinic_facility) return `Their clinic is now ${r.clinic_facility.name || 'a ratified building'}`;
  if (r.proposed_facility) return `A manifest listed them at ${r.proposed_facility.name || 'a building'}`;
  if (r.clinic_id) return clinicPhrase(r);
  return 'No clinic on file';
}

/** A provider added them from CounselEAR at a building (2026-10-28): the provider's word, in one sentence. */
function providerAddedWords(r: NoBuildingResident): string | null {
  const a = r.provider_added;
  if (!a) return null;
  const where = a.facility.name || 'a building';
  return `${a.by || 'A provider'} added them from CounselEAR at ${where} on ${when(a.at)} and says they are at ${where}.`;
}

function WhereCell({ r }: { r: NoBuildingResident }) {
  if (r.provider_added && !r.clinic_facility) {
    return <ToneChip tone="attn" title={providerAddedWords(r) || ''}>Provider says: {r.provider_added.facility.name || 'a building'}</ToneChip>;
  }
  if (r.clinic_facility) return <ToneChip tone="ok" title="The clinic was ratified as this building after the resident was created. A person records the move.">{r.clinic_facility.name || 'Ratified building'}</ToneChip>;
  if (r.proposed_facility) return <ToneChip tone="attn" title="One building's manifest listed this resident. Proposed, not confirmed.">Proposed: {r.proposed_facility.name || 'a building'}</ToneChip>;
  return <ToneChip tone="quiet" title={r.clinic_id ? 'The resident\'s CounselEAR clinic has not been confirmed as an eMerge building. Set it under "By CounselEAR clinic".' : 'CounselEAR named no clinic.'}>{clinicPhrase(r)}</ToneChip>;
}

const SORTERS: Record<string, (r: NoBuildingResident) => string | number | null> = {
  since: (r) => new Date(r.created_at).getTime(),
  clinic: (r) => clinicOf(r),
};

/** Residents per page (the server's limit). "Show more" fetches the next page (Copilot review of #402, 4266772334). */
export const NO_BUILDING_PAGE = 100;

export default function NoBuildingDesk() {
  const [data, setData] = useState<NoBuildingList | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const [filter, setFilter] = useState('');
  const [pages, setPages] = useState(0);
  const [more, setMore] = useState<'idle' | 'loading' | 'failed'>('idle');

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await noBuildingApi.list(1, NO_BUILDING_PAGE);
      if (r.success && r.data) { setData(r.data); setPages(1); }
      else setProblem(refusalWords(r, 'Could not load the residents with no building.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /** The next page, appended; a resident already shown (the list moved between loads) is not shown twice. */
  const loadMore = useCallback(async () => {
    if (!data) return;
    setMore('loading');
    try {
      const r = await noBuildingApi.list(pages + 1, NO_BUILDING_PAGE);
      if (!r.success || !r.data) { setMore('failed'); return; }
      const next = r.data;
      setData((prev) => {
        if (!prev) return next;
        const seen = new Set(prev.items.map((x) => x.id));
        return { ...prev, total: next.total, items: [...prev.items, ...next.items.filter((x) => !seen.has(x.id))] };
      });
      setPages((n) => n + 1);
      setMore('idle');
    } catch { setMore('failed'); }
  }, [data, pages]);

  const all = data?.items || [];
  const q = filter.trim().toLowerCase();
  const shown = q ? all.filter((r) => `${residentLabel(r)} ${r.counselear_id || ''} ${r.clinic_id || ''} ${r.clinic_name || ''}`.toLowerCase().includes(q)) : all;
  const sort = useClientSort<NoBuildingResident>(shown, SORTERS);

  const columns: BoardColumn<NoBuildingResident>[] = [
    { key: 'ce', label: 'CounselEAR id', help: 'The resident\'s id in CounselEAR.', render: (r) => (r.counselear_id ? <span>{r.counselear_id}</span> : <span className="board-na">not on file</span>) },
    { key: 'clinic', label: 'Clinic', sort: 'clinic', help: 'The resident\'s CounselEAR Preferred Clinic. Its building, once ratified, is where new residents are placed.', render: (r) => (r.clinic_id ? <span title={`CounselEAR clinic ${r.clinic_id}`}>{clinicOf(r)}</span> : <span className="board-na">none</span>) },
    { key: 'where', label: 'Where they might live', help: 'What eMerge knows: the building their clinic was ratified as since, a manifest that listed them, or nothing yet.', render: (r) => <WhereCell r={r} /> },
    { key: 'twl', label: 'TWL', help: 'Whether CounselEAR tags the resident TWL.', render: (r) => (r.twl ? <ToneChip tone="attn">TWL</ToneChip> : <span className="board-na">—</span>) },
    { key: 'case', label: 'Open case', help: 'Whether a Patient Advocate case is open for them. A case cannot be assigned until the building is confirmed.', render: (r) => (r.open_case ? <ToneChip tone="stop">Open, held for a building</ToneChip> : <span className="board-na">none</span>) },
    { key: 'origin', label: 'Created by', help: 'What created the resident in eMerge. A clinic\'s confirmation places them whatever created them.', render: (r) => <span>{ORIGIN_WORDS[r.creation_origin || ''] || 'not recorded'}</span> },
    { key: 'since', label: 'In eMerge since', sort: 'since', help: 'The day eMerge created the resident.', render: (r) => <span>{when(r.created_at)}</span> },
  ];

  const identity = {
    label: 'Resident',
    help: 'A resident CounselEAR holds that eMerge created with no confirmed building.',
    render: (r: NoBuildingResident) => <ResidentName p={r} />,
    name: (r: NoBuildingResident) => residentLabel(r),
  };

  const sections = (r: NoBuildingResident): BoardDetailSection[] => [{
    id: 'where', label: 'Where they live',
    render: () => (
      <div className="mf-panel">
        <p>{whereWords(r)}.</p>
        {r.provider_added && (
          <p role="note">
            {providerAddedWords(r)} Setting their CounselEAR clinic&apos;s building places them; if their clinic is not that
            building, ask the provider and check the Preferred Clinic in CounselEAR.
          </p>
        )}
        <dl className="mf-facts">
          <dt>CounselEAR id</dt><dd>{r.counselear_id || <span className="board-na">not on file</span>}</dd>
          <dt>Preferred Clinic</dt><dd>{r.clinic_id ? `${clinicOf(r)}${r.clinic_name ? ` (CounselEAR ${r.clinic_id})` : ''}` : <span className="board-na">none</span>}</dd>
          <dt>Clinic&apos;s building</dt><dd>{r.clinic_facility ? (r.clinic_facility.name || 'ratified') : <span className="board-na">not ratified yet</span>}</dd>
          <dt>Manifest</dt><dd>{r.proposed_facility ? `listed at ${r.proposed_facility.name || 'a building'} (proposed)` : <span className="board-na">not listed</span>}</dd>
        </dl>
        <p>
          {r.clinic_id && !r.clinic_facility
            ? 'Setting this clinic\'s building (By CounselEAR clinic, above the list) places this resident there, with every resident of the clinic who has no building, whoever created them, and every new one.'
            : r.clinic_facility
              ? 'Their clinic\'s building is confirmed; setting it again (By CounselEAR clinic) places them there.'
              : 'CounselEAR named no clinic for this resident: a person places them, with a Patient Advocate\'s facility move.'}
        </p>
      </div>
    ),
  }];

  let state: React.ReactNode = null;
  if (problem) state = <MfLoadFailure message={problem} retry={load} />;
  else if (!data) state = <MfSkeleton bare />;
  else if (data.enterprise_only) state = <TideState kind="empty" reason="none" kindLabel="Not your list" message="Residents with no building belong to no facility, so only a reviewer for every building sees them." />;
  else if (all.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Everyone placed" message="Every resident with a CounselEAR clinic has a building." />;

  const total = data && !data.enterprise_only ? data.total : null;
  return (
    <main className="mf-main mf-main--board">
      <ProviderStatements />
      <NoBuildingClinics onChanged={load} />
      <MfBoard<NoBuildingResident>
        title="No building"
        count={total}
        units={['resident', 'residents']}
        what="Residents with no confirmed building, so no building's manifest shows them. A resident's building is their CounselEAR Preferred Clinic's: set a clinic's building above, and its residents are placed there."
        controls={data && all.length > 0 ? (
          <input type="search" aria-label="Find a resident, CounselEAR id or clinic" placeholder="Find a resident, CounselEAR id or clinic" value={filter} onChange={(e) => setFilter(e.target.value)} />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={sort.rows}
        rowKey={(r) => r.id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${residentLabel(r)}: where they live`}
        sort={sort.sort}
        dir={sort.dir}
        sorts={sort.sorts}
        onSort={sort.onSort}
        foot={total !== null && total > all.length ? (
          <div className="board-more">
            <p aria-live="polite">Showing {all.length} of {total}. {q ? 'The search covers the residents shown; show more to search the rest.' : ''}</p>
            <button type="button" className="board-btn" onClick={loadMore} disabled={more === 'loading'}>{more === 'loading' ? 'Loading…' : 'Show more'}</button>
            {more === 'failed' && <p role="alert">Could not load more. Try again.</p>}
          </div>
        ) : total !== null && all.length > 0 ? <p aria-live="polite">Showing all {all.length}.</p> : null}
        notes={['"Name not recorded": CounselEAR held no name when eMerge read it; nothing is invented.']}
      />
    </main>
  );
}
